/* ==========================================================================
   UltraWheels — predictive-search.js
   Live search suggestions rendered by sections/predictive-search.liquid.
   ========================================================================== */

(function () {
  'use strict';

  const MIN_LENGTH = 2;
  const DEBOUNCE = 250;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.clearButton = this.querySelector('[data-predictive-clear]');
      if (!this.input || !this.results) return;

      this.debounceTimer = null;
      this.abortController = null;
      this.cache = new Map();
      this.isOpen = false;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.clearButton) {
        this.clearButton.addEventListener('click', this.onClear.bind(this));
      }

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onDocumentClick);

      this.toggleClearButton();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      clearTimeout(this.debounceTimer);
      if (this.abortController) this.abortController.abort();
    }

    get terms() {
      return this.input.value.trim();
    }

    onInput() {
      this.toggleClearButton();
      clearTimeout(this.debounceTimer);

      if (this.terms.length < MIN_LENGTH) {
        this.close();
        return;
      }

      this.debounceTimer = setTimeout(() => this.search(this.terms), DEBOUNCE);
    }

    onFocus() {
      if (this.suppressFocusOpen) return;
      if (this.terms.length >= MIN_LENGTH && this.results.innerHTML.trim()) this.open();
    }

    onClear() {
      this.input.value = '';
      this.toggleClearButton();
      this.close();
      this.input.focus();
    }

    toggleClearButton() {
      if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
    }

    async search(terms) {
      if (this.cache.has(terms)) {
        this.render(this.cache.get(terms));
        return;
      }

      // Abort a slower in-flight request so results can't arrive out of order.
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.classList.add('is-loading');
      this.setStatus(this.dataset.loadingText || '');

      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product,page,article',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });

      try {
        const url = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
        const response = await fetch(`${url}?${params}`, {
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(response.status);

        const markup = new DOMParser()
          .parseFromString(await response.text(), 'text/html')
          .querySelector('.shopify-section').innerHTML;

        this.cache.set(terms, markup);
        this.render(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Fall back to the full search page rather than leaving a dead dropdown.
        this.close();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(markup) {
      this.results.innerHTML = markup;

      if (!markup.trim()) {
        this.close();
        return;
      }

      this.open();

      const count = this.results.querySelectorAll('.predictive-search__item').length;
      const summary = this.results.querySelector('.predictive-search__count');
      this.setStatus(summary ? summary.textContent.trim() : String(count));
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      document.body.classList.add('predictive-search-open');
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('predictive-search-open');
      this.activeIndex = -1;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        // focus() fires synchronously and would re-open via onFocus.
        this.suppressFocusOpen = true;
        this.input.focus();
        this.suppressFocusOpen = false;
        this.close();
        return;
      }

      if (!this.isOpen) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const links = Array.from(this.results.querySelectorAll('a'));
      if (links.length === 0) return;

      event.preventDefault();
      const current = links.indexOf(document.activeElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // Wrap around; from the input (current === -1) ArrowUp lands on the last item.
      const next = (current + step + links.length + 1) % (links.length + 1);

      if (next === links.length) {
        this.input.focus();
      } else {
        links[next].focus();
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
